import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { portalLinks } from "@/lib/sofmat-navigation";

export default function Users() {
  const { user, tenant } = useAuth();
  const sofmat = user?.auth_provider === "sofmat_sso";
  const canManage = user?.permissions?.manage_users ?? ["owner", "admin"].includes(user?.role);
  const [error, setError] = useState("");
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", role: "estimator", name: "" });
  const [inviteToken, setInviteToken] = useState(null);

  const load = async () => { try { const { data } = await api.get("/users"); setList(data); setError(""); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Impossibile caricare gli utenti"); } };
  useEffect(() => { load(); }, []);

  const invite = async (e) => { e.preventDefault();
    try {
      const { data } = await api.post("/users/invite", f);
      setInviteToken(data.token);
      toast.success("Invito creato");
      setF({ email: "", role: "estimator", name: "" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const updateRole = async (id, role) => {
    try { await api.put(`/users/${id}`, { role }); toast.success("Ruolo aggiornato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const toggleActive = async (id, active) => {
    try { await api.put(`/users/${id}`, { active }); toast.success("Aggiornato"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="p-8" data-testid="users-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Team</div>
          <h1 className="text-3xl font-display font-bold mt-1">Utenti & Ruoli</h1>
        </div>
        {sofmat ? <Button asChild disabled={!canManage} data-testid="btn-invite-user"><a href={portalLinks(tenant).team}>Invita dal team SOFMAT →</a></Button> : <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button disabled={!canManage} data-testid="btn-invite-user">+ Invita utente</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invita utente</DialogTitle></DialogHeader>
            <form onSubmit={invite} className="space-y-3">
              <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({...f, email: e.target.value})} required data-testid="invite-email" /></div>
              <div><Label>Nome (opz.)</Label><Input value={f.name} onChange={(e) => setF({...f, name: e.target.value})} /></div>
              <div><Label>Ruolo</Label>
                <Select value={f.role} onValueChange={(v) => setF({...f, role: v})}>
                  <SelectTrigger data-testid="invite-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="estimator">Estimator</SelectItem>
                    <SelectItem value="sales">Sales</SelectItem>
                    <SelectItem value="viewer">Viewer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full" data-testid="invite-save">Genera invito</Button>
              {inviteToken && (
                <div className="p-2 bg-muted border border-border text-xs font-mono break-all">
                  <div className="text-muted-foreground mb-1">Link di invito (copia e invia):</div>
                  /accept-invite?token={inviteToken}
                </div>
              )}
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      {error && <p role="alert" className="mb-4 text-red-700">{error}</p>}
      {sofmat && <p className="mb-4 text-sm">Inviti e membri aziendali si gestiscono dal team SOFMAT, entro il limite del pacchetto. Qui assegni i permessi QuoteOS ai collaboratori che hanno già effettuato l’accesso.</p>}
      {!canManage && <p className="mb-4">La gestione dei ruoli è disponibile per titolari e amministratori.</p>}
      <div className="border border-border bg-white">
        <table className="dense-table w-full">
          <thead><tr><th>Nome</th><th>Email</th><th>Ruolo</th><th>Attivo</th><th>Creato</th></tr></thead>
          <tbody>
            {list.map(u => (
              <tr key={u.id}>
                <td>{u.name}</td><td className="font-mono text-xs">{u.email}</td>
                <td>
                  <Select value={u.role} disabled={!canManage || u.role_management_locked || (user?.role !== "owner" && u.role === "owner")} onValueChange={(v) => updateRole(u.id, v)}>
                    <SelectTrigger className="w-32 h-7"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(user?.role === "owner" ? ["owner","admin","estimator","sales","viewer"] : ["admin","estimator","sales","viewer"]).map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </td>
                <td><button disabled={!canManage || u.role_management_locked || (user?.role !== "owner" && u.role === "owner")} onClick={() => toggleActive(u.id, !u.active)} className={`text-xs px-2 py-0.5 border ${u.active ? "bg-reliability-green" : "bg-reliability-red"}`}>{u.active ? "Attivo" : "Disattivo"}</button></td>
                <td className="text-xs text-muted-foreground">{(u.created_at||"").slice(0,10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


